import { Alert, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { useDarkMode } from '../contexts/DarkModeContext'
import { roleHintKey, roleTitleKey } from '../utils/helpers/sharing.helpers'

interface SharedExperienceRoleBoxProps {
  /** Role slug as served by `get-user-experiences` — 'listener', not 3. */
  role: string | null | undefined
}

/**
 * Tells the recipient of a shared experience what they may do with it, in the
 * second person. These few lines are the ONLY explanation they will get: there
 * is no documentation and no onboarding.
 *
 * Renders nothing when there is nothing true to say — no role, an unknown slug,
 * or `owner`, who does not need to be told what they can do with their own
 * experience (AC-6). No empty box, no "unknown role" placeholder.
 */
const SharedExperienceRoleBox: React.FC<SharedExperienceRoleBoxProps> = ({ role }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  const titleKey = roleTitleKey(role)
  const hintKey = roleHintKey(role)

  if (!titleKey || !hintKey) {
    return null
  }

  return (
    <Alert
      variant={darkMode ? 'outlined' : 'ghost'}
      color="blue-gray"
      data-testid="shared-experience-role-box"
    >
      <Typography className="font-bold" data-testid="shared-experience-role-title">
        {t(titleKey)}
      </Typography>
      <Typography variant="small" data-testid="shared-experience-role-hint">
        {t(hintKey)}
      </Typography>
    </Alert>
  )
}

export default SharedExperienceRoleBox
